import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import { useTranslation } from "react-i18next";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import FileItem from "./FileItem";
import { RecycleBinFileNameCell } from "./RecycleBinItem";
import RecycleBinItemActions from "./RecycleBinItemActions";
import CircularProgress from "@mui/material/CircularProgress";
import { formatFileSize, formatDate } from "../utils/fileOperations";

// Define table columns for header alignment only
const headCells = [
  { id: "name", label: "Name" },
  { id: "type", label: "Type" },
  { id: "owner", label: "Owner" },
  { id: "size", label: "Size" },
  { id: "modified", label: "Modified" },
];

function EnhancedTableHead(props) {
  const { order, orderBy, viewMode, onSortChange } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));
  const isSm = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const translation = t("fileExplorer");
  const headCells = [
    { id: "name", label: translation.sortByName },
    { id: "type", label: translation.type },
    { id: "owner", label: translation.owner },
    { id: "size", label: translation.sortBySize },
    { id: "modified", label: translation.modified },
  ];

  const createSortHandler = (property) => () => {
    onSortChange(property);
  };

  // Define which columns to show based on screen size
  const getVisibleColumns = () => {
    if (isXs) {
      return ["name"]; // Only show name on extra small screens
    }
    if (isSm) {
      return ["name", "size", "modified"]; // Show name, size, and modified on small screens
    }
    return ["name", "type", "owner", "size", "modified"]; // Show all columns on medium and larger screens
  };

  const visibleColumns = getVisibleColumns();

  return (
    <TableHead>
      <TableRow>
        <TableCell padding="none">
          <Box sx={{ display: "flex", alignItems: "center", py: 1.5, px: 2 }}>
            {/* Icon spacer */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: isXs ? 32 : 40,
                mr: isXs ? 1 : 2,
              }}
            />

            {/* Name - Always visible */}
            <Box
              sx={{
                flex: "1 1 auto",
                mr: isXs ? 1 : 2,
                minWidth: 0,
                minWidth: isXs ? 100 : "auto",
              }}
            >
              <TableSortLabel
                active={orderBy === "name"}
                direction={orderBy === "name" ? order : "asc"}
                onClick={createSortHandler("name")}
                hideSortIcon={false}
              >
                {headCells[0].label}
              </TableSortLabel>
            </Box>

            {/* Type - Hidden on xs and sm */}
            {visibleColumns.includes("type") && (
              <Box
                sx={{
                  flex: "0 0 100px",
                  mr: 2,
                }}
              >
                <TableSortLabel
                  active={orderBy === "type"}
                  direction={orderBy === "type" ? order : "asc"}
                  onClick={createSortHandler("type")}
                  hideSortIcon={false}
                >
                  {headCells[1].label}
                </TableSortLabel>
              </Box>
            )}

            {/* Owner - Hidden on xs and sm */}
            {visibleColumns.includes("owner") && (
              <Box
                sx={{
                  flex: "0 0 120px",
                  mr: 2,
                }}
              >
                <TableSortLabel
                  active={orderBy === "owner"}
                  direction={orderBy === "owner" ? order : "asc"}
                  onClick={createSortHandler("owner")}
                  hideSortIcon={false}
                >
                  {headCells[2].label}
                </TableSortLabel>
              </Box>
            )}

            {/* Size - Hidden on xs */}
            {visibleColumns.includes("size") && (
              <Box
                sx={{
                  flex: isSm ? "0 0 60px" : "0 0 80px",
                  mr: isXs ? 1 : 2,
                }}
              >
                <TableSortLabel
                  active={orderBy === "size"}
                  direction={orderBy === "size" ? order : "asc"}
                  onClick={createSortHandler("size")}
                  hideSortIcon={false}
                >
                  {headCells[3].label}
                </TableSortLabel>
              </Box>
            )}

            {/* Modified - Hidden on xs */}
            {visibleColumns.includes("modified") && (
              <Box
                sx={{
                  flex: isSm ? "0 0 100px" : "0 0 120px",
                  mr: 1,
                }}
              >
                <TableSortLabel
                  active={orderBy === "modified"}
                  direction={orderBy === "modified" ? order : "asc"}
                  onClick={createSortHandler("modified")}
                  hideSortIcon={false}
                >
                  {headCells[4].label}
                </TableSortLabel>
              </Box>
            )}

            {/* Actions spacer - ensure enough space for menu on ALL screen sizes */}
            <Box
              sx={{
                minWidth: 48,
                display: "flex",
                justifyContent: "flex-end",
                flexShrink: 0, // Prevent shrinking on any screen size
              }}
            />
          </Box>
        </TableCell>
      </TableRow>
    </TableHead>
  );
}

function RecycleBinTableHead({ order, orderBy, onSortChange }) {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const columns = [
    { id: "name", label: "Name" },
    { id: "size", label: "Size" },
    { id: "deleted_at", label: "Deleted At" },
    { id: "actions", label: "" },
  ];

  // On small screens, hide size and deleted_at columns
  const visibleColumns = isXs
    ? columns.filter((col) => ["name", "actions"].includes(col.id))
    : columns;

  const createSortHandler = (property) => () => {
    if (property !== "actions") onSortChange(property);
  };

  return (
    <TableHead>
      <TableRow>
        {visibleColumns.map((col) => (
          <TableCell
            key={col.id}
            align={col.id === "actions" ? "right" : "left"}
            sx={{
              ...(col.id === "name" && isXs && { width: "60%" }),
              ...(col.id === "actions" && isXs && { width: "40%" }),
            }}
          >
            {col.id !== "actions" ? (
              <TableSortLabel
                active={orderBy === col.id}
                direction={orderBy === col.id ? order : "asc"}
                onClick={createSortHandler(col.id)}
                hideSortIcon={false}
              >
                {col.label}
              </TableSortLabel>
            ) : null}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

export default function FileExplorerTable({
  items = [],
  viewMode = "fileExplorer",
  onSortChange,
  onPageChange,
  onRowsPerPageChange,
  onItemClick = () => {},
  onFolderNavigate,
  onRenameFolder,
  onDeleteFolder,
  getFiles,
  user,
  onActionComplete,
  isSelected = () => false,
  loading = false,
  page: controlledPage,
  setPage: controlledSetPage,
  limit: controlledLimit,
  rowsPerPage: controlledRowsPerPageAlt,
  totalItems,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const [rows, setRows] = React.useState([]);
  const [localTotalItems, setLocalTotalItems] = React.useState(0);
  // Use controlled or local state
  const [localPage, setLocalPage] = React.useState(0);
  const [localRowsPerPage, setLocalRowsPerPage] = React.useState(5);
  const controlledRowsPerPage = controlledLimit || controlledRowsPerPageAlt;
  const page =
    controlledPage !== undefined
      ? viewMode === "recycleBin"
        ? Math.max(0, controlledPage - 1)
        : controlledPage
      : localPage; // TablePagination is 0-based
  const setPage = controlledSetPage || setLocalPage;
  const rowsPerPage = controlledRowsPerPage || localRowsPerPage;
  const setRowsPerPage = controlledRowsPerPage ? () => {} : setLocalRowsPerPage;
  const [sortBy, setSortBy] = React.useState("name");
  const [sortOrder, setSortOrder] = React.useState("asc");

  // Organize items: folders first, then files
  const organizedItems = React.useMemo(() => {
    const folders = items.filter(
      (item) => item.type === "folder" || item.isFolder
    );
    const files = items.filter(
      (item) => item.type !== "folder" && !item.isFolder
    );
    return [...folders, ...files];
  }, [items]);

  // Pagination calculations
  const emptyRows =
    page > 0
      ? Math.max(0, (1 + page) * rowsPerPage - organizedItems.length)
      : 0;

  const handleChangePage = (event, newPage) => {
    setPage(
      controlledSetPage
        ? viewMode === "recycleBin"
          ? newPage + 1
          : newPage
        : newPage
    );
    onPageChange && onPageChange(event, newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value, 10);
    setRowsPerPage(value);
    setPage(viewMode === "recycleBin" ? 1 : 0); // Reset to first page
    onRowsPerPageChange && onRowsPerPageChange(event);
  };

  const handleSortChange = (column) => {
    const isAsc = sortBy === column && sortOrder === "asc";
    const nextOrder = isAsc ? "desc" : "asc";
    setSortOrder(nextOrder);
    setSortBy(column);
    setPage(viewMode === "recycleBin" ? 1 : 0);
    onSortChange && onSortChange(column, nextOrder);
  };

  // Folder-aware click: navigate folders, open sidebar for files
  const handleItemClick = (item, event) => {
    const isFolder = item?.type === "folder" || item?.isFolder;
    if (isFolder) {
      if (onFolderNavigate) onFolderNavigate(item);
      if (getFiles && typeof getFiles === "function") {
        try {
          getFiles(item.id);
        } catch (e) {}
      }
    } else {
      onItemClick(item, event);
    }
  };

  React.useEffect(() => {
    setRows(organizedItems);
    setLocalTotalItems(
      totalItems !== undefined ? totalItems : organizedItems.length
    );
  }, [organizedItems, totalItems]);

  return (
    <Box sx={{ width: "100%", height: "100%" }}>
      <Paper
        sx={{
          width: "100%",
          height: "100%",
          mb: 2,
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <TableContainer
          sx={{
            flex: 1,
            overflowX: "auto", // allow horizontal scroll on small screens
          }}
        >
          <Table
            sx={{
              minWidth: 0,
              width: "100%",
            }}
            aria-labelledby="tableTitle"
            size="medium"
            stickyHeader
          >
            {viewMode === "recycleBin" ? (
              <RecycleBinTableHead
                order={sortOrder}
                orderBy={sortBy}
                onSortChange={onSortChange || handleSortChange}
              />
            ) : (
              <EnhancedTableHead
                order={sortOrder}
                orderBy={sortBy}
                viewMode={viewMode}
                onSortChange={handleSortChange}
              />
            )}
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell
                    colSpan={viewMode === "recycleBin" ? (isXs ? 2 : 4) : 1}
                    padding="none"
                  >
                    <Box
                      sx={{ py: 6, display: "flex", justifyContent: "center" }}
                    >
                      <CircularProgress size={24} />
                    </Box>
                  </TableCell>
                </TableRow>
              ) : viewMode === "recycleBin" ? (
                rows.map((row, index) => {
                  if (isXs) {
                    // Simplified view for extra small screens
                    return (
                      <TableRow
                        hover
                        tabIndex={-1}
                        key={row.id}
                        selected={isSelected(row)}
                        sx={{ cursor: "default" }}
                      >
                        <TableCell sx={{ width: "60%" }}>
                          <RecycleBinFileNameCell
                            item={row}
                            isSelected={isSelected(row)}
                          />
                          {/* Show size and date inline on small screens */}
                          <Box
                            sx={{
                              fontSize: "0.75rem",
                              color: "text.secondary",
                              mt: 0.5,
                              display: "flex",
                              gap: 1,
                            }}
                          >
                            <span>{formatFileSize(row.size)}</span>
                            <span>•</span>
                            <span>
                              {row.deleted_at
                                ? formatDate(row.deleted_at)
                                : "-"}
                            </span>
                          </Box>
                        </TableCell>
                        <TableCell align="right" sx={{ width: "40%" }}>
                          <RecycleBinItemActions
                            item={row}
                            user={user}
                            onActionComplete={onActionComplete}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  }

                  // Normal view for larger screens
                  return (
                    <TableRow
                      hover
                      tabIndex={-1}
                      key={row.id}
                      selected={isSelected(row)}
                      sx={{ cursor: "default" }}
                    >
                      <TableCell>
                        <RecycleBinFileNameCell
                          item={row}
                          isSelected={isSelected(row)}
                        />
                      </TableCell>
                      <TableCell>{formatFileSize(row.size)}</TableCell>
                      <TableCell>
                        {row.deleted_at ? formatDate(row.deleted_at) : "-"}
                      </TableCell>
                      <TableCell align="right">
                        <RecycleBinItemActions
                          item={row}
                          user={user}
                          onActionComplete={onActionComplete}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                rows.map((row, index) => {
                  const labelId = `enhanced-table-checkbox-${index}`;
                  return (
                    <TableRow
                      hover
                      tabIndex={-1}
                      key={row.id}
                      selected={isSelected(row)}
                      sx={{ cursor: "pointer" }}
                    >
                      <TableCell
                        component="th"
                        id={labelId}
                        scope="row"
                        padding="none"
                      >
                        <FileItem
                          item={row}
                          isSelected={isSelected(row)}
                          onClick={(item, event) =>
                            handleItemClick(item, event)
                          }
                          onRenameFolder={onRenameFolder}
                          onDeleteFolder={onDeleteFolder}
                          viewType="list"
                          // Ensure actions are always visible
                          showActions={true}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
              {emptyRows > 0 && !loading && (
                <TableRow style={{ height: 53 * emptyRows }}>
                  <TableCell
                    colSpan={viewMode === "recycleBin" ? (isXs ? 2 : 4) : 1}
                  />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 2,
            backgroundColor: "background.paper",
            borderTop: 1,
            borderColor: "divider",
          }}
        >
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={localTotalItems}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelDisplayedRows={({ from, to, count, page }) => {
              const totalPages =
                rowsPerPage > 0
                  ? Math.max(1, Math.ceil(count / rowsPerPage))
                  : 1;
              return `${from}-${to} of ${count} (Page ${
                page + 1
              } of ${totalPages})`;
            }}
            // Make pagination more compact on small screens
            sx={{
              ".MuiTablePagination-toolbar": {
                paddingLeft: isXs ? 1 : 2,
                paddingRight: isXs ? 1 : 2,
              },
              ".MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows":
                {
                  fontSize: isXs ? "0.75rem" : "0.875rem",
                },
            }}
          />
        </Box>
      </Paper>
    </Box>
  );
}

FileExplorerTable.propTypes = {
  items: PropTypes.array,
  viewMode: PropTypes.string,
  onItemClick: PropTypes.func,
  onFolderNavigate: PropTypes.func,
  onRenameFolder: PropTypes.func,
  onDeleteFolder: PropTypes.func,
  getFiles: PropTypes.func,
  user: PropTypes.object,
  onActionComplete: PropTypes.func,
  isSelected: PropTypes.func,
  loading: PropTypes.bool,
  page: PropTypes.number,
  setPage: PropTypes.func,
  limit: PropTypes.number,
  rowsPerPage: PropTypes.number,
  totalItems: PropTypes.number,
};
